// Magnetic declination from the World Magnetic Model 2025 — a degree-12 spherical-harmonic model,
// evaluated the way NOAA's reference C library (GeomagnetismLibrary.c) does it, ported from the
// Android app's Wmm2025.kt: geodetic to geocentric coordinates, Schmidt semi-normalised Legendre
// functions, the field summed in spherical components and rotated back to geodetic north/east.
// Every row of NOAA's WMM2025_TestValues.txt is pinned in the tests.

import { WMM_EPOCH, WMM_MAX_DEGREE, WMM_TABLE } from './wmm2025Coefficients';
import { toDegrees, toRadians } from './geodesy';

/** Valid model span; dates outside are clamped to the nearest end. */
export const WMM_VALID_FROM = 2025.0;
export const WMM_VALID_TO = 2030.0;

const REFERENCE_RADIUS_KM = 6371.2;
const SEMI_MAJOR_AXIS_KM = 6378.137;
const ECCENTRICITY_SQUARED = 0.0066943799901413169961;

const TERM_COUNT = (WMM_MAX_DEGREE + 1) * (WMM_MAX_DEGREE + 2) / 2;

const index = (n: number, m: number): number => n * (n + 1) / 2 + m;
const clamp = (value: number, min: number, max: number): number => Math.min(Math.max(value, min), max);

const g = new Float64Array(TERM_COUNT);
const h = new Float64Array(TERM_COUNT);
const gDot = new Float64Array(TERM_COUNT);
const hDot = new Float64Array(TERM_COUNT);
/** Gauss → Schmidt quasi-normalisation factors, fixed for the model's degree. */
const schmidtNorm = new Float64Array(TERM_COUNT);

WMM_TABLE.split('\n')
  .map((line) => line.trim())
  .filter((line) => line.length > 0)
  .forEach((line) => {
    const fields = line.split(/\s+/);
    const i = index(parseInt(fields[0], 10), parseInt(fields[1], 10));
    g[i] = parseFloat(fields[2]);
    h[i] = parseFloat(fields[3]);
    gDot[i] = parseFloat(fields[4]);
    hDot[i] = parseFloat(fields[5]);
  });

schmidtNorm[0] = 1.0;
for (let n = 1; n <= WMM_MAX_DEGREE; n++) {
  schmidtNorm[index(n, 0)] = schmidtNorm[index(n - 1, 0)] * (2 * n - 1) / n;
  for (let m = 1; m <= n; m++) {
    const factor = (n - m + 1) * (m === 1 ? 2.0 : 1.0) / (n + m);
    schmidtNorm[index(n, m)] = schmidtNorm[index(n, m - 1)] * Math.sqrt(factor);
  }
}

/**
 * Schmidt semi-normalised associated Legendre functions P(n,m)(x) and their latitude derivatives,
 * x = sin(geocentric latitude). MAG_PcupLow: Gauss-normalised recursion, then the normalisation
 * factors, with the derivative's sign flipped for spherical coordinates.
 */
function legendre(x: number): [Float64Array, Float64Array] {
  const p = new Float64Array(TERM_COUNT);
  const dp = new Float64Array(TERM_COUNT);
  p[0] = 1.0;
  dp[0] = 0.0;
  const z = Math.sqrt((1.0 - x) * (1.0 + x));
  for (let n = 1; n <= WMM_MAX_DEGREE; n++) {
    for (let m = 0; m <= n; m++) {
      const i = index(n, m);
      if (n === m) {
        const i1 = index(n - 1, m - 1);
        p[i] = z * p[i1];
        dp[i] = z * dp[i1] + x * p[i1];
      } else if (n === 1 && m === 0) {
        const i1 = index(0, 0);
        p[i] = x * p[i1];
        dp[i] = x * dp[i1] - z * p[i1];
      } else {
        const i2 = index(n - 1, m);
        if (m > n - 2) {
          p[i] = x * p[i2];
          dp[i] = x * dp[i2] - z * p[i2];
        } else {
          const i1 = index(n - 2, m);
          const k = ((n - 1) * (n - 1) - m * m) / ((2 * n - 1) * (2 * n - 3));
          p[i] = x * p[i2] - k * p[i1];
          dp[i] = x * dp[i2] - z * p[i2] - k * dp[i1];
        }
      }
    }
  }
  for (let i = 1; i < TERM_COUNT; i++) {
    p[i] *= schmidtNorm[i];
    dp[i] = -dp[i] * schmidtNorm[i];
  }
  return [p, dp];
}

/** Geodetic north (X) and east (Y) components in nT. */
export function horizontalField(
  latitude: number,
  longitude: number,
  altitudeKm: number,
  decimalYear: number,
): { north: number; east: number } {
  // The pole itself has no defined east; nudge off it rather than special-casing the sums.
  const lat = clamp(latitude, -89.999_999, 89.999_999);
  const dt = clamp(decimalYear, WMM_VALID_FROM, WMM_VALID_TO) - WMM_EPOCH;

  // Geodetic → geocentric spherical (MAG_GeodeticToSpherical).
  const latRad = toRadians(lat);
  const sinLat = Math.sin(latRad);
  const cosLat = Math.cos(latRad);
  const rc = SEMI_MAJOR_AXIS_KM / Math.sqrt(1.0 - ECCENTRICITY_SQUARED * sinLat * sinLat);
  const xp = (rc + altitudeKm) * cosLat;
  const zp = (rc * (1.0 - ECCENTRICITY_SQUARED) + altitudeKm) * sinLat;
  const r = Math.sqrt(xp * xp + zp * zp);
  const geocentricLat = Math.asin(zp / r);

  const lambda = toRadians(longitude);
  const cosMLambda = new Float64Array(WMM_MAX_DEGREE + 1);
  const sinMLambda = new Float64Array(WMM_MAX_DEGREE + 1);
  for (let m = 0; m <= WMM_MAX_DEGREE; m++) {
    cosMLambda[m] = Math.cos(m * lambda);
    sinMLambda[m] = Math.sin(m * lambda);
  }
  const [p, dp] = legendre(Math.sin(geocentricLat));

  let bx = 0.0;
  let by = 0.0;
  let bz = 0.0;
  for (let n = 1; n <= WMM_MAX_DEGREE; n++) {
    const radiusPower = Math.pow(REFERENCE_RADIUS_KM / r, n + 2);
    for (let m = 0; m <= n; m++) {
      const i = index(n, m);
      const gnm = g[i] + dt * gDot[i];
      const hnm = h[i] + dt * hDot[i];
      const cosTerm = gnm * cosMLambda[m] + hnm * sinMLambda[m];
      bz -= radiusPower * cosTerm * (n + 1) * p[i];
      by += radiusPower * (gnm * sinMLambda[m] - hnm * cosMLambda[m]) * m * p[i];
      bx -= radiusPower * cosTerm * dp[i];
    }
  }
  const cosGeocentric = Math.cos(geocentricLat);
  by = Math.abs(cosGeocentric) > 1e-10 ? by / cosGeocentric : 0.0;

  // Rotate the spherical components to the geodetic frame (MAG_RotateMagneticVector).
  const psi = geocentricLat - latRad;
  const north = bx * Math.cos(psi) - bz * Math.sin(psi);
  return { north, east: by };
}

/**
 * Declination in degrees, east positive, at a geodetic position [altitudeKm] above the WGS84
 * ellipsoid on [decimalYear] (clamped to the model's 2025.0–2030.0 span).
 */
export function declination(lat: number, lng: number, altitudeKm: number, decimalYear: number): number {
  const { north, east } = horizontalField(lat, lng, altitudeKm, decimalYear);
  return toDegrees(Math.atan2(east, north));
}

/** 2026-07-02T12:00Z → ≈ 2026.5. UTC, with the year's actual length (leap years included). */
export function decimalYear(date: Date): number {
  const millis = date.getTime();
  const year = date.getUTCFullYear();
  const start = Date.UTC(year, 0, 1);
  const end = Date.UTC(year + 1, 0, 1);
  return year + (millis - start) / (end - start);
}

/** Declination at sea level for the instant [date]. */
export function declinationAt(lat: number, lng: number, date: Date): number {
  return declination(lat, lng, 0.0, decimalYear(date));
}
